import type React from "react";
import API_HOST, { HEADERS_JSON, METHOD_POST, XHR_HEADER } from "../../../ApiConfig";
import DataTable from "react-data-table-component";
import BarraMenu from "../../../components/BarraMenu";
import PageHero from "../../../components/PageHero";
import useDigitalTheme from "../../hooks/useDigitalTheme";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { httpApiPostText } from "../../../lib";
import { FaPencilAlt, FaUndoAlt, FaPlus, FaUsersCog } from "react-icons/fa";
import MsgDialog from "../../../components/MsgDialog";
import sha256 from "crypto-js/sha256";
import "animate.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { businessThemes, getBusinessGroupName } from "../../../theme/businessThemes";
import { Modal, Button, Form } from "react-bootstrap";
import "./style.css";
// ═══════════════════════════════════════════════════════════════════════════
// HELPERS DE MÓDULO — extraídos para reducir complejidad cognitiva (SonarQube T4/T6)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Parsea JSON de forma segura sin lanzar excepciones
 */
const parseJsonSafe = (text: string): any | null => {
  try { return JSON.parse(text); } catch { return null; }
};

/**
 * Realiza un POST a la API
 * Centraliza el patrón fetch repetido en ColaboradoresPage (SonarQube T4)
 */
const apiPostColaboradores = async (host: string, body: object): Promise<any | null> => {
  try {
    const res = await fetch(host, {
      method: METHOD_POST,
      headers: { ...HEADERS_JSON, ...XHR_HEADER },
      body: JSON.stringify(body),
    });
    return parseJsonSafe(await res.text());
  } catch { return null; }
};

/**
 * Genera email temporal para líderes sin email
 * Extraído de guardar() para reducir complejidad (SonarQube T4)
 */
const generateTempLeaderEmail = (nombres: string, apellidos: string): string => {
  const sanitizedName     = nombres.toLowerCase().replace(/[^a-z0-9]/g, "").substring(0, 10);
  const sanitizedLastName = apellidos.toLowerCase().replace(/[^a-z0-9]/g, "").substring(0, 10);
  return `lider_${sanitizedName}_${sanitizedLastName}@samtel.co`;
};

/**
 * Construye la firma de intento para debounce de guardar()
 * Extraído de guardar() para reducir complejidad (SonarQube T4)
 */
const buildSubmissionSignature = (title: string, formData: any): string =>
  `${title}-${formData.email}-${formData.nombres}-${formData.apellidos}-${formData.lider}`;

// ═══════════════════════════════════════════════════════════════════════════



// ── TODO SonarQube TIPO 4/6: Refactorización pendiente ──────────────────────
// Candidatos a extraer:
//   - useColaboradoresData(): hook para lógica de carga y CRUD
//   - ColaboradorRow: fila de tabla con acciones
//   - ColaboradorModal: modal de creación/edición
//   - buildColaboradorPayload(): construir payload de API
// ────────────────────────────────────────────────────────────────────────────


const userResponse = {
  id_user: 0,
  name: "",
  profile: "",
  client: "",
  states: "",
  user_registered: "",
  user_email: "",
};

// Mejora OWASP A1: Inyección - Validación de entrada mejorada
function validateEmail(email: string) {
  // Expresión regular más robusta para validar emails
  const validEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return validEmail.test(email);
}

// Mejora OWASP A7: XSS - Función para sanitizar entradas
function sanitizeInput(input: string): string {
  if (!input) return "";
  return input
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
}

// ── Helper: cargar catálogos si están vacíos (SonarQube T4: reducir complejidad) ──
// ── Helper: construir body para newUser (SonarQube T4: reducir CC de guardar) ─
const buildNewUserBody = (formData: any, evalText: string): object => {
  const currentDate = new Date().toISOString().split('T')[0];
  return {
    valid: 'newUser',
    user_login: formData.email,
    user_pass: formData.email,
    user_email: formData.email,
    first_name: formData.nombres,
    last_name: formData.apellidos,
    user_profile: formData.perfil,
    user_job: formData.cargo,
    id_client: formData.cliente === '' ? 0 : Number(formData.cliente),
    user_registered: formData.fingreso || currentDate,
    user_evaluation_date: formData.fUltimaEval || currentDate,
    id_evaluator: formData.evaluador === '' ? 0 : formData.evaluador,
    idbusiness_group: formData.empresa === '' ? 0 : Number(formData.empresa),
    user_leader: formData.lider || '',
  };
};

// ── Helper: construir body para updateUser (SonarQube T4: reducir CC de guardar) ─
const buildUpdateUserBody = (formData: any): object => ({
  valid: 'updateUser',
  id_user: formData.id_user,
  user_login: formData.email,
  user_pass: '',
  user_email: formData.email,
  first_name: formData.nombres,
  last_name: formData.apellidos,
  user_profile: formData.perfil,
  user_client: formData.cliente,
  user_registered: formData.fingreso || '0000-00-00',
  user_evaluation_date: formData.fUltimaEval || '0000-00-00',
  user_job: formData.cargo,
  states: formData.states,
  id_client: formData.cliente === '' ? 0 : Number(formData.cliente),
  id_evaluator: formData.evaluador === '' ? 0 : formData.evaluador,
  idbusiness_group: formData.empresa === '' ? 0 : Number(formData.empresa),
  user_leader: formData.lider || '',
});

// ── Helper: resetear formulario tras guardado exitoso ────────────────────────
const resetFormularioColaborador = () => ({
  id_user: '', nombres: '', apellidos: '', cargo: '', cliente: '',
  fingreso: '', fUltimaEval: '', email: '', perfil: '',
  evaluador: '', empresa: '', lider: '', states: 'activo',
});


const cargarCatalogosParaEdicion = async (
  perfil: any[], companies: any[], clientes: any[], cargo: any[],
  getApiUrl: () => string | undefined,
  setPerfil: any, setCompanies: any, setClientes: any, setCargo: any
) => {
  const url = getApiUrl();
  if (!url) return;
  const postCatalog = async (valid: string) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ valid }),
    });
    const txt = await res.text();
    return JSON.parse(txt);
  };
  if (!perfil || perfil.length === 0) {
    try {
      const d = await postCatalog('getSelectProfiles');
      setPerfil(d.payload);
    } catch {}
  }
  if (!companies || companies.length === 0) {
    try {
      const d = await postCatalog('getSelectCompany');
      setCompanies(Array.isArray(d?.payload) ? d.payload.filter((x: any) => x.status === 'activo') : []);
    } catch { setCompanies([]); }
  }
  if (!clientes || clientes.length === 0) {
    try {
      const d = await postCatalog('getSelectClients');
      setClientes(d.payload);
    } catch { setClientes([]); }
  }
  if (!cargo || cargo.length === 0) {
    try {
      const d = await postCatalog('getSelectUserJob');
      setCargo(d.payload);
    } catch { setCargo([]); }
  }
};

// ── Helper: configurar restricciones UI según perfil (SonarQube T4) ───────────
const configurarRestriccionesPerfilUI = (
  perfilText: string,
  setClnt: any, setEvalua: any
) => {
  if (perfilText === 'administrador')     { setClnt(true);  setEvalua(true);  return; }
  if (perfilText === 'evaluador')          { setEvalua(true); setClnt(false);  return; }
  if (perfilText === 'evaluador externo')  { setEvalua(true); setClnt(false);  return; }
  if (perfilText === 'lider')              { setEvalua(true); setClnt(true);   return; }
  setClnt(false); setEvalua(false);
};


// ── guardar: lógica separada por rama (SonarQube T4: reducir CC) ─────────────
// ── Helper: construir formData desde payload del usuario (SonarQube T4) ──────
const buildFormDataFromPayload = (p: any, idbusiness_group: any): any => ({
  id_user: p.id_user ?? '',
  nombres: p.first_name ?? '',
  apellidos: p.last_name ?? '',
  cargo: p.user_job ? String(p.user_job) : '',
  cliente: p.client ? String(p.client) : '',
  fingreso: p.user_registered ? p.user_registered.substring(0, 10) : '',
  fUltimaEval: p.user_evaluation_date ? p.user_evaluation_date.substring(0, 10) : '',
  email: p.user_email ?? '',
  perfil: p.user_profile ? String(p.user_profile) : '',
  evaluador: p.id_evaluator ? String(p.id_evaluator) : '',
  empresa: String(p.idbusiness_group ?? p.id_business_group ?? idbusiness_group ?? ''),
  lider: p.user_leader ?? '',
  states: p.states ?? 'activo',
});


// ── guardar: pre-validación extraída (SonarQube T4) ─────────────────────────
const guardarPreValidar = (
  formData: any, evalText: string, title: string,
  generateTempLeaderEmail: (n:string,a:string)=>string
): { fd: any; skip: boolean } => {
  let fd = { ...formData };
  if (evalText === 'lider' && !fd.email.trim()) {
    fd = { ...fd, email: generateTempLeaderEmail(fd.nombres, fd.apellidos) };
  }
  if (title === ' Guardar ') {
    if (fd.fingreso) {
      const [y,m,d] = fd.fingreso.split('-').map(Number);
      const next = new Date(y+1,m-1,d);
      fd = { ...fd, fUltimaEval: next.toISOString().split('T')[0] };
    } else {
      fd = { ...fd, fUltimaEval: '' };
    }
  }
  return { fd, skip: false };
};


const guardarNuevoUsuario = async (
  formData: any, evalText: string, colaboradoresAsignados: any[],
  accesos: any[], url: string,
  setRespuestaMsg: any, setRespuestaShow: any,
  resetAll: () => void
) => {
  const bdy = buildNewUserBody(formData, evalText);
  console.log('[DEBUG newUser] Body enviado:', JSON.stringify(bdy));
  const response = await httpApiPostText(url, METHOD_POST, { ...HEADERS_JSON, ...XHR_HEADER }, bdy);
  console.log('[DEBUG newUser] Respuesta:', response.message);
  const dta = JSON.parse(response.message);
  setRespuestaMsg(dta.message); setRespuestaShow(true);
  if (dta.code !== 1) return;
  const newId = dta.payload?.id_user || dta.id_user;
  // Guardar user_access para cualquier perfil que tenga cliente asignado
  if (formData.cliente && formData.perfil) {
    await httpApiPostText(API_HOST, "POST", HEADERS_JSON, {
      valid: "saveUserAccess", id_user: newId,
      accesos: [{ id_client: formData.cliente, id_profile: formData.perfil, idbusiness_group: formData.empresa || "1" }],
    });
  }
  resetAll();
};

const guardarUpdateUsuario = async (
  formData: any, evalText: string, colaboradoresAsignados: any[],
  accesos: any[], perfil: any[], url: string,
  setRespuestaMsg: any, setRespuestaShow: any,
  resetAll: () => void
) => {
  const bdy = buildUpdateUserBody(formData);
  const response = await httpApiPostText(url, "PUT", { ...HEADERS_JSON, ...XHR_HEADER }, bdy);
  const dta = JSON.parse(response.message);

  const accesosNormalizados = (Array.isArray(accesos) ? accesos : [])
    .map((a: any) => ({
      id_client: String(a?.id_client ?? formData.cliente ?? "").trim(),
      id_profile: String(a?.id_profile ?? formData.perfil ?? "").trim(),
      idbusiness_group: String(a?.idbusiness_group ?? formData.empresa ?? "1").trim(),
      client_name: a?.client_name || "",
      profile_name: a?.profile_name || "",
      id_evaluator: a?.id_evaluator ? String(a.id_evaluator) : undefined,
      evaluator_name: a?.evaluator_name || undefined,
    }))
    .filter((a: any) => a.id_client && a.id_profile && a.idbusiness_group);

  const accesosParaGuardar =
    accesosNormalizados.length > 0
      ? accesosNormalizados
      : (evalText === "evaluador externo" && formData.cliente && formData.perfil
          ? [{
              id_client: String(formData.cliente),
              id_profile: String(formData.perfil),
              idbusiness_group: String(formData.empresa || "1"),
              client_name: "",
              profile_name: "",
              id_evaluator: undefined as string | undefined,
              evaluator_name: undefined as string | undefined,
            }]
          : []);

  const respAccess = await httpApiPostText(API_HOST, "POST", HEADERS_JSON, {
    valid: "saveUserAccess",
    id_user: formData.id_user,
    accesos: accesosParaGuardar,
  });
  const dtaAccess = JSON.parse(respAccess.message);
  if (dtaAccess.code !== 1) {
    setRespuestaMsg(dtaAccess.message || "No se pudieron guardar los accesos");
    setRespuestaShow(true);
    return;
  }

  const accesosColab = accesosParaGuardar.filter((a: any) => {
    const p = perfil.find((pr: any) => String(pr.id) === a.id_profile);
    return p?.description?.toLowerCase() === "colaborador";
  });
  for (const ac of accesosColab) {
    if (ac.id_client && ac.id_evaluator) {
      await httpApiPostText(API_HOST, "POST", HEADERS_JSON, {
        valid: "saveColaboradorRelation", id_user: formData.id_user,
        id_client: ac.id_client, id_evaluator: ac.id_evaluator,
      });
    }
  }
  if (evalText === "evaluador externo" && colaboradoresAsignados.length > 0) {
    await httpApiPostText(API_HOST, "POST", HEADERS_JSON, {
      valid: "saveCollaboratorsExterno", id_evaluator: formData.id_user,
      id_client: formData.cliente, collaborators: colaboradoresAsignados,
    });
  }
  setRespuestaMsg(dta.message); setRespuestaShow(true);
  resetAll();
};



const GESTION_USUARIOS_STATE_KEY = "gestionUsuariosState";
const loadGestionUsuariosState = () => {
  try { return JSON.parse(sessionStorage.getItem(GESTION_USUARIOS_STATE_KEY) || "{}"); } catch { return {}; }
};

const ColaboradoresPage = () => {
  useDigitalTheme();
  // Estado de los datos
  const [data, setData] = useState(new Array());
  const [cpData, setCpData] = useState(new Array());
  const [perfil, setPerfil] = useState(new Array());
  const [cargo, setCargo] = useState(new Array());
  const [clientes, setClientes] = useState(new Array());
  const [companies, setCompanies] = useState(new Array());
  const [evaluators, setEvaluators] = useState(new Array());
  const [leaders, setLeaders] = useState(new Array());
  const initialGestionState = useMemo(() => loadGestionUsuariosState(), []);
  const [filtroCargo, setFiltroCargo] = useState(initialGestionState.filtroCargo || "");
  const [filtroPerfil, setFiltroPerfil] = useState(initialGestionState.filtroPerfil || "");
  const [filtroGrupo, setFiltroGrupo] = useState(initialGestionState.filtroGrupo || "");
  const [filtroCliente, setFiltroCliente] = useState(initialGestionState.filtroCliente || "");
  const [filtroEstado, setFiltroEstado] = useState(initialGestionState.filtroEstado || "");
  const [filtroNombre, setFiltroNombre] = useState("");
  const emp: any = useSelector((state: any) => state.emp);
  const { idbusiness_group } = emp;
  const theme = businessThemes[idbusiness_group || 1] || businessThemes[1];

  const normalizeText = (value: any): string =>
    String(value ?? "").toLowerCase().trim();

  const getEvaluatorProfileText = (ev: any): string =>
    normalizeText(
      ev.profile ??
        ev.user_profile ??
        ev.profile_name ??
        ev.profile_description ??
        ev.description
    );

  const isEvaluatorProfile = (ev: any): boolean => {
    const profileText = getEvaluatorProfileText(ev);
    if (profileText) {
      return profileText.includes("evaluador");
    }
    // Si el backend ya filtra evaluadores, permitimos cuando no venga texto
    return true;
  };

  let [formData, setFormData] = useState({
    id_user: "",
    nombres: "",
    apellidos: "",
    cargo: "",
    cliente: "",
    fingreso: "",
    fUltimaEval: "",
    email: "",
    perfil: "",
    evaluador: "",
    empresa: "",
    lider: "",
    states: "activo",
  });
  // Todos los evaluadores activos (perfil con "evaluador"); sin filtrar por grupo
  const filteredEvaluators = useMemo(() => {
    return (Array.isArray(evaluators) ? evaluators : []).filter((ev: any) =>
      isEvaluatorProfile(ev)
    );
  }, [evaluators]);
  const [borrarShow, setBorrarShow] = useState(false);
  const [respuestaShow, setRespuestaShow] = useState(false);
  const [respuestaMsg, setRespuestaMsg] = useState("");
  const [userSelected, setUserSelected] = useState(userResponse);
  const [msgActivacion, setMsgActivacion] = useState("");
  const [clnt, setClnt] = useState(false);
  const [evalua, setEvalua] = useState(false);
  const [title, setTitle] = useState(" Guardar ");
  const [evalText, setEvalText] = useState("");
  const [perfilHint, setPerfilHint] = useState("");
  // Mejora OWASP A6: Configuración de seguridad incorrecta - Control de estado de carga
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"add"|"edit">("add");
  // Protección contra envíos duplicados: firma del último submit y su timestamp
  const [lastSubmissionHash, setLastSubmissionHash] = useState("");
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);
  // Accesos (user_access)
  const [accesos, setAccesos] = useState<{id_client: string, id_profile: string, idbusiness_group: string, client_name: string, profile_name: string, id_evaluator?: string, evaluator_name?: string}[]>([]);
  const [nuevoAccesoGrupo, setNuevoAccesoGrupo] = useState("");
  const [nuevoAccesoPerfil, setNuevoAccesoPerfil] = useState("");
  const [nuevoAccesoEvaluador, setNuevoAccesoEvaluador] = useState("");
  const [evaluadoresPorGrupo, setEvaluadoresPorGrupo] = useState<any[]>([]);

  // Colaboradores asignados (evaluador externo)
  const [colaboradoresAsignados, setColaboradoresAsignados] = useState<{id_user: string, collaborator_name: string, user_email: string}[]>([]);
  const [busquedaColaborador, setBusquedaColaborador] = useState("");
  const [resultadosBusqueda, setResultadosBusqueda] = useState<any[]>([]);

  // Mejora OWASP A3: Exposición de datos sensibles - Uso de HTTPS
  const getApiUrl = () => {
    const apiUrl = API_HOST;
    if (!apiUrl) {
      console.error("URL de API no configurada");
      return "";
    }
    // Asegurar que se use HTTPS en producción
    return apiUrl;
  };

  // Tabla de datos
  const columns = [
    {
      name: "Nombre",
      selector: (row: any) => row.name,
      sortable: true,
      width: "220px",
    },
    {
      name: "Cargo",
      selector: (row: any) => {
        return row.job_user;
      },
      sortable: true,
      width: "220px",
    },
    {
      name: "Perfil",
      selector: (row: any) => row.profile,
      sortable: true,
      width: "140px",
    },
    {
      name: "Correo",
      selector: (row: any) => row.user_email,
      sortable: true,
      width: "200px",
      omit: true,
    },
    {
      name: "Grupo de Negócio",
      selector: (row: any) => getBusinessGroupName(row.idbusiness_group ?? row.id_business_group ?? row.business_group ?? ""),
      sortable: true,
      width: "180px",
    },
    {
      name: "Cliente",
      selector: (row: any) => row.client,
      sortable: true,
      width: "140px",
    },
    {
      name: "Estado",
      selector: (row: any) => row.states,
      sortable: true,
      width: "100px",
    },
    {
      name: "Evaluador",
      selector: (row: any) => row.name_evaluator,
      sortable: true,
      width: "130px",
      omit: true,
    },
    {
      name: "Líder",
      selector: (row: any) => row.name_leader,
      sortable: true,
      width: "130px",
      omit: true,
    },
    {
      name: "Acciones",
      selector: (row: any) => row.Acciones,
      right: true,
      width: "120px",
      sortable: true,
    },
  ];

  const pagOptions = {
    rowsPerPageText: "Filas por páginas",
    rangeSeparatorText: "de",
    selectAllRowsItem: true,
    selectAllRowsItemText: "Todos",
  };

  const conditionalRowStyles = [
    {
      when: (row: { states: string }) => row.states === "inactivo",
      style: {
        backgroundColor: "#D5D5D5",
      },
    },
  ];

  // Mejora OWASP A10: Registro y monitoreo insuficientes - Manejo seguro de errores
  function sumarDias(fecha: any, dias: number) {
    try {
      if (!fecha) return "";

      // Validar formato de fecha
      if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
        console.error("Formato de fecha inválido:", fecha);
        return "";
      }

      const dia = Number.parseInt(fecha.substring(8, 10)) + 1;
      const mes = fecha.substring(5, 7);
      const anno = fecha.substring(0, 4);
      let fec = `${anno}-${mes}-${dia}`;
      const newFecha = new Date(fec);
      newFecha.setDate(newFecha.getDate() + dias);

      fec = newFecha.toLocaleDateString();
      const diaa =
        newFecha.getDate() + 1 < 10
          ? `0${newFecha.getDate() + 1}`
          : `${newFecha.getDate() + 1}`;
      const mesa =
        newFecha.getMonth() + 1 < 10
          ? `0${newFecha.getMonth() + 1}`
          : `${newFecha.getMonth() + 1}`;
      const annoa = newFecha.getFullYear();
      fec = `${annoa}-${mesa}-${diaa}`;

      return fec;
    } catch (error) {
      console.error("Error al calcular fecha:", error);
      return "";
    }
  }

  // Mejora OWASP A2: Autenticación rota - Encabezados de seguridad
  const getUsers = async () => {
    setIsLoading(true);
    try {
      const url = getApiUrl();
      if (!url) {
        setRespuestaMsg("Error de configuración: URL de API no disponible");
        setRespuestaShow(true);
        return;
      }

      const response = await httpApiPostText(
        url, METHOD_POST, {
          ...HEADERS_JSON,
          ...XHR_HEADER, // Prevenir CSRF
        },
        { 
          valid: "allUser",
          logged_user_id: emp.id_user,
          logged_user_profile: emp.profile,
          logged_user_business_group: emp.idbusiness_group
        }
      );

      if (!response || !response.message) {
        throw new Error("Respuesta inválida del servidor");
      }

      const dta = JSON.parse(response.message);
      const payload = Array.isArray(dta.payload) ? dta.payload : [];
      
      // El filtrado ahora se hace en el backend, no es necesario filtrar aquí
      const newData = new Array();

      payload.map((dta: any) => {
        const obj = {
          ...dta,
          Acciones: (
            <div className="d-flex gap-3">
              {/* Mejora OWASP A8: Deserialización insegura - Uso de botones en lugar de enlaces */}
              <button
                className="btn btn-sm p-0"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  editarUsuario(dta.id_user);
                }}
                aria-label="Editar usuario"
              >
                <FaPencilAlt
                  title="Editar"
                  color={"white"}
                  className="bg-warning rounded-circle p-1 h4"
                />
              </button>
              <button
                className="btn btn-sm p-0"
                onClick={() => desactivaActvaUser(dta)}
                aria-label="Cambiar estado de usuario"
              >
                <FaUndoAlt
                  title="Cambia de estado"
                  color={"white"}
                  className="bg-danger rounded-circle p-1 h4"
                />
              </button>
            </div>
          ),
        };
        newData.push(obj);
      });

      setData([...newData]);
      setCpData([...newData]);
    } catch (error) {
      console.error("Error al obtener usuarios:", error);
      setRespuestaMsg("Error al cargar los usuarios. Intente nuevamente.");
      setRespuestaShow(true);
    } finally {
      setIsLoading(false);
    }
  };

  const getPerfil = async () => {
    try {
      const url = getApiUrl();
      if (!url) return;

      const response = await httpApiPostText(
        url, METHOD_POST, {
          ...HEADERS_JSON,
          ...XHR_HEADER,
        },
        { valid: "getSelectProfiles" }
      );

      const dta = JSON.parse(response.message);
      setPerfil(dta.payload);
    } catch (error) {
      console.error("Error al obtener perfiles:", error);
    }
  };

  const getCliente = async () => {
    try {
      const url = getApiUrl();
      if (!url) return;

      const response = await httpApiPostText(
        url, METHOD_POST, {
          ...HEADERS_JSON,
          ...XHR_HEADER,
        },
        { valid: "getSelectClients" }
      );

      const dta = JSON.parse(response.message);
      console.log('[getCliente] Clientes cargados:', dta.payload);
      setClientes(dta.payload);
    } catch (error) {
      console.error("Error al obtener clientes:", error);
    }
  };

  const getCompanies = async () => {
    try {
      const url = getApiUrl();
      if (!url) return;

      const response = await httpApiPostText(
        url, METHOD_POST, {
          ...HEADERS_JSON,
          ...XHR_HEADER,
        },
        { valid: "getSelectCompany" }
      );

      const dta = JSON.parse(response.message);
      if (dta && dta.payload && Array.isArray(dta.payload)) {
        const companies = dta.payload.filter((item: any) => item.status === "activo");
        setCompanies(companies);
      } else {
        console.warn("Invalid payload received:", dta);
        setCompanies([]);
      }
    } catch (error) {
      console.error("Error al obtener empresas:", error);
      setCompanies([]);
    }
  };

  const getEvaluators = async (_idbusiness_group?: string) => {
    try {
      const url = getApiUrl();
      if (!url) return;

      // Siempre traer todos los evaluadores activos (sin filtrar por grupo)
      const bdy: Record<string, any> = {
        valid: "selectEvaluators",
      };

      const response = await httpApiPostText(
        url, METHOD_POST, {
          ...HEADERS_JSON,
          ...XHR_HEADER,
        },
        bdy
      );

      const dta = JSON.parse(response.message);
      setEvaluators(Array.isArray(dta.payload) ? dta.payload : []);
    } catch (error) {
      console.error("Error al obtener evaluadores:", error);
      setEvaluators([]);
    }
  };

  const getLeaders = async () => {
    try {
      const url = getApiUrl();
      if (!url) return;

      const bdy = {
        valid: "selectLeaders",
      };

      const response = await httpApiPostText(
        url, METHOD_POST, {
          ...HEADERS_JSON,
          ...XHR_HEADER,
        },
        bdy
      );

      const dta = JSON.parse(response.message);
      setLeaders(dta.payload);
    } catch (error) {
      console.error("Error al obtener líderes:", error);
    }
  };
  /* Este constante se encarga de editar el usuario */
  const editarUsuario = async (id: any) => {
    if (!id) return;

    setIsLoading(true);
    console.log('emp datos:', emp);
    console.log('API URL:', getApiUrl());
    try {
      // cargarCatalogosParaEdicion extraído (SonarQube T4: reducir complejidad)
      let perfiles = perfil;
      await cargarCatalogosParaEdicion(
        perfil, companies, clientes, cargo,
        getApiUrl, (p: any) => { perfiles = p; setPerfil(p); },
        setCompanies, setClientes, setCargo
      );

      const url = getApiUrl();
      if (!url) return;

      const bdy = {
        valid: "selectUser",
        id_user: `${id}`,
      };

      const response = await httpApiPostText(
        url, METHOD_POST, {
          ...HEADERS_JSON,
          ...XHR_HEADER,
        },
        bdy
      );

      const dta = JSON.parse(response.message);

      if (!dta.payload || dta.payload.length === 0) {
        setRespuestaMsg("No se pudo obtener la información del usuario");
        setRespuestaShow(true);
        return;
      }

      // Debug: Log para verificar qué datos trae el backend
      console.log('[editarUsuario] Datos del usuario:', dta.payload[0]);
      console.log('[editarUsuario] Cliente recibido:', dta.payload[0].client);

      formData = buildFormDataFromPayload(dta.payload[0], idbusiness_group);

      if (emp.profile === "Evaluador") {
        formData.empresa = idbusiness_group ? String(idbusiness_group) : "";
      }

      console.log('[editarUsuario] FormData antes de setFormData:', formData);
      console.log('[editarUsuario] Cliente en formData:', formData.cliente);

      setFormData({ ...formData });

      // Cargar todos los evaluadores activos para el selector
      await getEvaluators();

      // Configurar las restricciones basadas en el perfil del usuario
      const perfilId = String(dta.payload[0].user_profile ?? "");
      // Buscar el perfil correspondiente para obtener su descripción
      const perfilObj = perfiles.find((p: any) => String(p.id) === perfilId);
      const perfilText = perfilObj
        ? perfilObj.description.toLowerCase().trim()
        : "";
      setEvalText(perfilText);

      if (perfilText === "evaluador externo" && !formData.evaluador) {
        formData.evaluador = JULIE_MANRIQUE_ID;
        setFormData({ ...formData });
      }

      // Papus aqui configura las restricciones según el perfil
      // configurarRestriccionesPerfilUI extraído (SonarQube T4: reducir complejidad)
      configurarRestriccionesPerfilUI(perfilText, setClnt, setEvalua);

      setTitle(" Actualizar ");
      setModalMode("edit");
      // Cargar accesos y colaboradores asignados
      await getAccesos(String(id));
      if (perfilText === "evaluador externo") {
        await getColaboradoresAsignados(String(id));
      } else {
        setColaboradoresAsignados([]);
      }
      setShowModal(true);
      getUsers();
    } catch (error) {
      console.error("Error al Editar usuario:", error);
      setRespuestaMsg("Error al obtener datos del usuario");
      setRespuestaShow(true);
    } finally {
      setIsLoading(false);
    }
  };

  const desactivaActvaUser = (user: any) => {
    if (!user || !user.id_user) {
      setRespuestaMsg("Error: Usuario no válido");
      setRespuestaShow(true);
      return;
    }

    setUserSelected(user);
    setMsgActivacion(
      user.states === "activo"
        ? `Usted va desactivar al usuario ${user.name}, ¿Está usted seguro de querer hacerlo?`
        : `Usted va activar al usuario ${user.name}, ¿Está usted seguro de querer hacerlo?`
    );
    setBorrarShow(true);
  };

  const borrarNo = () => {
    setBorrarShow(false);
  };

  const borrarSi = async () => {
    setBorrarShow(false);

    if (!userSelected || !userSelected.id_user) {
      setRespuestaMsg("Error: No se ha seleccionado un usuario válido");
      setRespuestaShow(true);
      return;
    }

    setIsLoading(true);
    try {
      const url = getApiUrl();
      if (!url) return;

      const response = await httpApiPostText(
        url,
        "PUT",
        {
          ...HEADERS_JSON,
          ...XHR_HEADER,
        },
        {
          valid: "deleteUser",
          id_user: `${userSelected.id_user}`,
        }
      );

      const dta = JSON.parse(response.message);
      setRespuestaMsg(dta.message);
      setRespuestaShow(true);
      // actualiza la grilla
      getUsers();
    } catch (error) {
      console.error("Error al cambiar estado de usuario:", error);
      setRespuestaMsg("Error al procesar la solicitud");
      setRespuestaShow(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Mejora OWASP A7: XSS - Sanitización de entrada
  const changeTextFiltro = (evnt: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = sanitizeInput(evnt.target.value.trim().toUpperCase());

    if (inputValue === "") {
      setData([...cpData]);
    } else {
      const reg = cpData.filter(
        (dato: {
          name?: string;
          job_user?: string;
          profile?: string;
          user_email?: string;
          client?: string;
          user_registered?: string;
          states?: string;
        }) => {
          const fields = [
            dato?.name,
            dato?.job_user,
            dato?.profile,
            dato?.user_email,
            dato?.client,
            dato?.user_registered,
            dato?.states,
          ];

          return fields.some((field) =>
            field?.toUpperCase().includes(inputValue)
          );
        }
      );

      setData([...reg]);
    }
  };
    useEffect(() => {
      let resultado = [...cpData];
        console.log('Primer registro:', resultado[0]);
        console.log('filtroCargo valor:', filtroCargo);
        if (filtroNombre)  resultado = resultado.filter((d: any) => `${d.name}`?.toLowerCase()?.trim()?.includes(filtroNombre.toLowerCase().trim()));
        if (filtroCargo) resultado = resultado.filter((d: any) => d.job_user === filtroCargo);
        if (filtroPerfil)  resultado = resultado.filter((d: any) => d.profile?.toLowerCase()?.trim()?.startsWith(filtroPerfil.toLowerCase().trim()));
        if (filtroGrupo)   resultado = resultado.filter((d: any) => String(d.idbusiness_group) === filtroGrupo);
        if (filtroCliente) resultado = resultado.filter((d: any) => d.client?.toLowerCase()?.trim()?.startsWith(filtroCliente.toLowerCase().trim()));
        if (filtroEstado)  resultado = resultado.filter((d: any) => d.states === filtroEstado);
    setData(resultado);

  }, [filtroNombre, filtroCargo, filtroPerfil, filtroGrupo, filtroCliente, filtroEstado, cpData]);
  
  // Mejora OWASP A7: XSS - Sanitización de entrada
  const select = async (evnt: React.ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = evnt.target;
    let nextFormData = { ...formData, [id]: value };

    if (id === "empresa") {
      // Al cambiar grupo no se recortan evaluadores: la lista ya incluye todos los activos
      nextFormData = { ...nextFormData, evaluador: "" };
      // No llamar getEvaluators(value): deben verse todos los evaluadores activos
    }

    setFormData({ ...nextFormData });
  };

  // Mejora OWASP A7: XSS - Sanitización de entrada
  const textChange = (evnt: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(evnt.target.value);
    formData = { ...formData, [evnt.target.id]: sanitizedValue };
    setFormData({ ...formData });
  };

  // Mejora OWASP A5: Control de acceso roto - Validación de formulario mejorada
  const validateForm = (profile: string): boolean => {
    let sw = 0;

    if (profile.toLowerCase() !== "lider" && !validateEmail(formData.email)) {
      setRespuestaMsg("El email no es válido. Por favor revisar");
      setRespuestaShow(true);
      return false;
    }

    if (profile.toLowerCase() === "administrador") {
      sw = formData.nombres.trim() === "" ? ++sw : sw;
      sw = formData.apellidos.trim() === "" ? ++sw : sw;
      sw = formData.email.trim() === "" ? ++sw : sw;
      sw = !validateEmail(formData.email) ? ++sw : sw;
      sw = formData.perfil === "" ? ++sw : sw;
      sw = formData.cargo === "" ? ++sw : sw;
      sw = formData.fingreso === "" ? ++sw : sw;
    } else if (profile.toLowerCase() === "evaluador") {
      sw = formData.nombres.trim() === "" ? ++sw : sw;
      sw = formData.apellidos.trim() === "" ? ++sw : sw;
      sw = formData.email.trim() === "" ? ++sw : sw;
      sw = !validateEmail(formData.email) ? ++sw : sw;
      sw = formData.perfil === "" ? ++sw : sw;
      sw = formData.cargo === "" ? ++sw : sw;
      sw = formData.empresa === "" ? ++sw : sw;
      sw = formData.cliente === "" ? ++sw : sw;
      sw = formData.fingreso === "" ? ++sw : sw;
    } else if (profile.toLowerCase() === "evaluador externo") {
      sw = formData.nombres.trim() === "" ? ++sw : sw;
      sw = formData.apellidos.trim() === "" ? ++sw : sw;
      sw = formData.email.trim() === "" ? ++sw : sw;
      sw = !validateEmail(formData.email) ? ++sw : sw;
      sw = formData.perfil === "" ? ++sw : sw;
      sw = formData.cargo === "" ? ++sw : sw;
      sw = formData.empresa === "" ? ++sw : sw;
      sw = formData.cliente === "" ? ++sw : sw;
      sw = formData.fingreso === "" ? ++sw : sw;
    } else if (profile.toLowerCase() === "lider") {
      sw = formData.nombres.trim() === "" ? ++sw : sw;
      sw = formData.apellidos.trim() === "" ? ++sw : sw;
      sw = formData.perfil === "" ? ++sw : sw;
      sw = formData.cargo === "" ? ++sw : sw;
    } else {
      sw = formData.nombres.trim() === "" ? ++sw : sw;
      sw = formData.apellidos.trim() === "" ? ++sw : sw;
      sw = formData.email.trim() === "" ? ++sw : sw;
      sw = !validateEmail(formData.email) ? ++sw : sw;
      sw = formData.perfil === "" ? ++sw : sw;
      sw = formData.cargo === "" ? ++sw : sw;
      sw = formData.empresa === "" ? ++sw : sw;
      sw = formData.cliente === "" ? ++sw : sw;
      sw = formData.evaluador === "" ? ++sw : sw;
      sw = formData.fingreso === "" ? ++sw : sw;
    }

    // Validar empresa y cliente nunca nulos
    const perfilNorm = profile.toLowerCase();
    if (['colaborador','evaluador','evaluador externo'].includes(perfilNorm)) {
      if (!formData.empresa || formData.empresa === '') {
        setRespuestaMsg("El Grupo de Negocio es obligatorio para este perfil.");
        setRespuestaShow(true); return false;
      }
      if (!formData.cliente || formData.cliente === '') {
        setRespuestaMsg("El Cliente es obligatorio para este perfil.");
        setRespuestaShow(true); return false;
      }
    }
    if (sw > 0) {
      const msgs: Record<string, string> = {
        'colaborador': "Completa: nombre, apellidos, email, cargo, empresa, cliente, evaluador y fecha de ingreso.",
        'evaluador': "Completa: nombre, apellidos, email, cargo, empresa, cliente y fecha de ingreso.",
        'evaluador externo': "Completa: nombre, apellidos, email, cargo, empresa, cliente y fecha de ingreso.",
        'administrador': "Completa: nombre, apellidos, email, cargo y fecha de ingreso.",
        'lider': "Completa: nombre, apellidos y cargo.",
      };
      setRespuestaMsg(msgs[perfilNorm] || "Por favor complete todos los campos requeridos");
      setRespuestaShow(true); return false;
    }

    return true;
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevenir múltiples envíos y bloquear inmediatamente (doble click / multi submit)
    if (isLoading) return;
    setIsLoading(true);

    // guardarPreValidar + buildSubmissionSignature (SonarQube T4: reducir CC)
    const { fd } = guardarPreValidar(formData, evalText, title, generateTempLeaderEmail);
    formData = fd; setFormData({ ...formData });
    try {
      const sig = buildSubmissionSignature(title, formData);
      if (sig === lastSubmissionHash && Date.now() - lastSubmissionTime < 5000) { setIsLoading(false); return; }
      setLastSubmissionHash(sig); setLastSubmissionTime(Date.now());
    } catch {}
    await new Promise(r => setTimeout(r, 50));
    if (!validateForm(evalText)) { setIsLoading(false); return; }

    try {
      const url = getApiUrl();
      if (!url) return;

      // Preparar datos para enviar
      // guardarNuevoUsuario/guardarUpdateUsuario (SonarQube T4: CC reducida)
      const resetAll = () => {
        formData = resetFormularioColaborador();
        setFormData({ ...formData }); setAccesos([]); setColaboradoresAsignados([]);
        getUsers(); getEvaluators(); getLeaders();
        if (title !== " Guardar ") { getPerfil(); getCliente(); getCargo(); getCompanies(); }
        setTitle(" Guardar "); setShowModal(false);
      };
      if (title === " Guardar ") {
        await guardarNuevoUsuario(formData, evalText, colaboradoresAsignados, accesos, url, setRespuestaMsg, setRespuestaShow, resetAll);
      } else {
        await guardarUpdateUsuario(formData, evalText, colaboradoresAsignados, accesos, perfil, url, setRespuestaMsg, setRespuestaShow, resetAll);
      }
    } catch (error) {
      console.error("Error al guardar usuario:", error);
      setRespuestaMsg("Error al procesar la solicitud");
      setRespuestaShow(true);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelar = () => {
    formData = {
      id_user: "",
      nombres: "",
      apellidos: "",
      cargo: "",
      cliente: "",
      fingreso: "",
      fUltimaEval: "",
      email: "",
      perfil: "",
      evaluador: "",
      empresa: "",
      lider: "",
      states: "activo",
    };
    setFormData({ ...formData });
    setTitle(" Guardar ");
    setShowModal(false);
  };
  const getAccesos = async (id_user: string) => {
  const response = await httpApiPostText(API_HOST, "POST", HEADERS_JSON, { valid: "getUserAccess", id_user });
  const dta = JSON.parse(response.message);
  if (dta.code === 1) {
    setAccesos(dta.payload.map((a: any) => ({
      id_client: String(a.id_client),
      id_profile: String(a.id_profile),
      idbusiness_group: String(a.idbusiness_group ?? a.id_business_group ?? ""),
      client_name: a.client_name || "",
      profile_name: a.profile_name || "",
    })));
  } else {
    setAccesos([]);
  }
};

const cargarEvaluadoresPorGrupo = async (ibg: string) => {
  if (!ibg) { setEvaluadoresPorGrupo([]); return; }
  try {
    // Misma fuente: todos los evaluadores activos (perfil con "evaluador")
    const res = await httpApiPostText(API_HOST, "POST", HEADERS_JSON,
      { valid: "selectEvaluators" });
    const dta = JSON.parse(res.message);
    setEvaluadoresPorGrupo(Array.isArray(dta.payload) ? dta.payload : []);
  } catch { setEvaluadoresPorGrupo([]); }
};

const agregarAcceso = () => {
  if (!nuevoAccesoGrupo || !nuevoAccesoPerfil) return;

  const grupoObj = companies.find((c: any) => String(c.idbusiness_group) === nuevoAccesoGrupo);
  const perfilObj = perfil.find((p: any) => String(p.id) === nuevoAccesoPerfil);
  const id_client_del_grupo = String(grupoObj?.id_client || "");

  // Si el perfil es Colaborador, el evaluador es obligatorio
  const esColaborador = perfilObj?.description?.toLowerCase() === "colaborador";
  if (esColaborador && !nuevoAccesoEvaluador) {
    alert("Para el perfil Colaborador debe seleccionar un evaluador.");
    return;
  }

  // Evitar duplicados
  const ya = accesos.find(a => a.idbusiness_group === nuevoAccesoGrupo && a.id_profile === nuevoAccesoPerfil);
  if (ya) return;

  const evaluadorObj = evaluadoresPorGrupo.find((e: any) => String(e.id_user) === nuevoAccesoEvaluador);

  setAccesos([...accesos, {
    id_client: id_client_del_grupo,
    id_profile: nuevoAccesoPerfil,
    idbusiness_group: nuevoAccesoGrupo,
    client_name: getBusinessGroupName(nuevoAccesoGrupo),
    profile_name: perfilObj?.description || "",
    id_evaluator: esColaborador ? nuevoAccesoEvaluador : undefined,
    evaluator_name: esColaborador ? (evaluadorObj?.evaluator || "") : undefined,
  }]);

  setNuevoAccesoGrupo("");
  setNuevoAccesoPerfil("");
  setNuevoAccesoEvaluador("");
  setEvaluadoresPorGrupo([]);
};

const eliminarAcceso = (index: number) => {
  setAccesos(accesos.filter((_, i) => i !== index));
};

const getColaboradoresAsignados = async (id_evaluator: string) => {
  const response = await httpApiPostText(API_HOST, "POST", HEADERS_JSON, { valid: "getCollaboratorsByEvaluator", id_evaluator });
  const dta = JSON.parse(response.message);
  if (dta.code === 1) {
    setColaboradoresAsignados(dta.payload.map((c: any) => ({
      id_user: String(c.id_user),
      collaborator_name: c.collaborator_name,
      user_email: c.user_email,
    })));
  } else {
    setColaboradoresAsignados([]);
  }
};

const buscarColaborador = async (search: string) => {
  if (search.length < 2) { setResultadosBusqueda([]); return; }
  const response = await httpApiPostText(API_HOST, "POST", HEADERS_JSON, {
    valid: "searchCollaborators",
    search,
    id_client: formData.cliente || null,
  });
  const dta = JSON.parse(response.message);
  setResultadosBusqueda(dta.code === 1 ? dta.payload : []);
  };

  const agregarColaboradorAsignado = (col: any) => {
  const ya = colaboradoresAsignados.find(c => c.id_user === String(col.id_user));
  if (ya) return;
    setColaboradoresAsignados([...colaboradoresAsignados, {
    id_user: String(col.id_user),
    collaborator_name: col.collaborator_name,
    user_email: col.user_email,
  }]);
    setBusquedaColaborador("");
    setResultadosBusqueda([]);
  };

  const eliminarColaboradorAsignado = (id_user: string) => {
    setColaboradoresAsignados(colaboradoresAsignados.filter(c => c.id_user !== id_user));
  };
  const getCargo = async () => {
    try {
      const url = getApiUrl();
      if (!url) return;

      const response = await httpApiPostText(
        url, METHOD_POST, {
          ...HEADERS_JSON,
          ...XHR_HEADER,
        },
        { valid: "getSelectUserJob" }
      );

      const dta = JSON.parse(response.message);
      setCargo(dta.payload);
    } catch (error) {
      console.error("Error al obtener cargos:", error);
    }
  };

  const JULIE_MANRIQUE_ID = "1288";

  const PERFIL_HINTS: Record<string, string> = {
    "colaborador": "💡 Colaborador: requiere empresa, cliente y evaluador asignado.",
    "evaluador": "💡 Evaluador: requiere empresa y cliente. Verá colaboradores de su cliente.",
    "evaluador externo": "💡 Evaluador Externo: requiere empresa y cliente. El evaluador por defecto es Julie Manrique.",
    "administrador": "💡 Administrador: acceso completo al sistema.",
    "lider": "💡 Líder: solo requiere nombre, apellidos y cargo.",
  };

  const onSelectPerfil = (evnt: React.ChangeEvent<HTMLSelectElement>) => {
    const index = evnt.target.selectedIndex;
    const selected = evnt.target.options[index].text;
    const perfilNorm = selected.toLowerCase().trim();
    const isEvaluadorExterno = perfilNorm === "evaluador externo";
    formData = {
      ...formData,
      [evnt.target.id]: evnt.target.value,
      cliente: "",
      evaluador: isEvaluadorExterno ? JULIE_MANRIQUE_ID : "",
    };
    setFormData({ ...formData });
    setEvalText(perfilNorm);
    setPerfilHint(PERFIL_HINTS[perfilNorm] || "");
    configurarRestriccionesPerfilUI(perfilNorm, setClnt, setEvalua);
  };

  useEffect(() => {
    getUsers();
    getPerfil();
    getCliente();
    getCompanies();
    getCargo();
    getEvaluators();
    getLeaders();

    // Mejora OWASP A6: Configuración de seguridad incorrecta - Limpieza al desmontar
    return () => {
      // Cleanup if needed
    };
  }, []);

  // Persistir estado de búsqueda
  useEffect(() => {
    sessionStorage.setItem(GESTION_USUARIOS_STATE_KEY, JSON.stringify({
      filtroCargo, filtroCliente, filtroPerfil, filtroGrupo, filtroEstado,
    }));
  }, [filtroCargo, filtroCliente, filtroPerfil, filtroGrupo, filtroEstado]);


  return (
    <>
      <div className="container bg-white gestion-colaboradores-container page-shell">
        <BarraMenu />
        <div className="page-shell__body">
        <PageHero
          icon={<FaUsersCog />}
          title="Panel de Gestión de Usuarios"
          subtitle="Sistema de administración de perfiles y roles"
          badge={
            <div className="dashboard-hero__badge">
              {data.length} colaboradores
            </div>
          }
        />

        {/* Mejora OWASP A6: Configuración de seguridad incorrecta - Indicador de carga */}
        {isLoading && (
          <div className="alert alert-info" role="alert">
            Cargando, por favor espere...
          </div>
        )}

      <div className="mb-3 d-flex align-items-end gap-3">
          <div className="d-flex gap-2 flex-wrap mb-2" style={{ flex: 1 }}>
            <div className="mb-2">
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por nombre o apellido..."
              value={filtroNombre}
              onChange={e => setFiltroNombre(e.target.value)}
             />
            </div>
          <select className="form-select" 
                style={{ maxWidth: "180px" }} 
                value={filtroCargo} 
                onChange={e => {
                console.log('cargo seleccionado:', e.target.value);
                console.log('cpData length:', cpData.length);
                setFiltroCargo(e.target.value);
}}
>
                <option value="">Todos los cargos</option>
                 {cargo.map((c: any) => (
                 <option value={c.description} key={c.id}>{c.description}</option>
                
            ))}

        </select>

        <select className="form-select" style={{ maxWidth: "180px" }} value={filtroPerfil} onChange={e => setFiltroPerfil(e.target.value)}>
          <option value="">Todos los perfiles</option>
            {perfil.map((p: any) => (
          <option value={p.description} key={p.id}>{p.description}</option>
        ))}
        </select>

        <select className="form-select" style={{ maxWidth: "200px" }} value={filtroGrupo} onChange={e => setFiltroGrupo(e.target.value)}>
          <option value="">Todos los grupos</option>
            {companies.map((c: any) => (
          <option value={String(c.idbusiness_group)} key={c.idbusiness_group}>{c.name}</option>
        ))}
        </select>

        <select className="form-select" style={{ maxWidth: "180px" }} value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)}>
          <option value="">Todos los clientes</option>
            {clientes.map((c: any) => (
          <option value={c.description} key={c.id}>{c.description}</option>
        ))}
        </select>

        <select className="form-select" style={{ maxWidth: "150px" }} value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="activo">Activo</option>
          <option value="inactivo">Inactivo</option>
        </select>
      </div>


          <Button 
            variant="success" 
            onClick={() => { 
              setModalMode("add");
              const empresaValue = String(idbusiness_group || emp.idbusiness_group || "1");
              formData = {
                id_user: "",
                nombres: "",
                apellidos: "",
                cargo: "",
                cliente: "",
                fingreso: "",
                fUltimaEval: "",
                email: "",
                perfil: "4",
                evaluador: "",
                empresa: empresaValue,
                lider: "",
                states: "activo",
              };
              setFormData({ ...formData });
              setTitle(" Guardar ");
              setClnt(false);
              setEvalua(false);
              setEvalText("colaborador");


              // Cargar todos los evaluadores activos para el selector
              getEvaluators();
              setAccesos([]);
              setColaboradoresAsignados([]);
              setBusquedaColaborador("");
              setResultadosBusqueda([]);
              setShowModal(true);
            }}
          >
            <FaPlus /> Nuevo Usuario
          </Button>
        </div>

        <div className="mb-3">
          <DataTable
            columns={columns}
            data={data}
            selectableRowsHighlight
            fixedHeader
            pagination
            highlightOnHover
            paginationComponentOptions={pagOptions}
            conditionalRowStyles={conditionalRowStyles}
            className="border shadow rounded"
            noDataComponent={
              <div className="p-4">No hay registros disponibles</div>
            }
            progressPending={isLoading}
            progressComponent={<div className="p-4">Cargando datos...</div>}
          />
        </div>
        </div>
      </div>

      {/*********** seccion de modales **********************/}
      {/************Modal para agregar/editar colaborador**********/}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === "add" ? "Nuevo Usuario" : "Editar Usuario"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={guardar}>
            <div className="row g-3">
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control
                    type="text"
                    id="nombres"
                    value={formData.nombres}
                    placeholder="Nombre"
                    onChange={textChange}
                    maxLength={100}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Apellidos</Form.Label>
                  <Form.Control
                    type="text"
                    id="apellidos"
                    value={formData.apellidos}
                    placeholder="Apellidos"
                    onChange={textChange}
                    maxLength={100}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Perfil</Form.Label>
                  <Form.Select
                    id="perfil"
                    onChange={onSelectPerfil}
                    value={formData.perfil}
                    required
                  >
                    <option value="">Seleccione un perfil...</option>
                    {perfil
                      .sort((a, b) =>
                        a.description.localeCompare(b.description, "es", {
                          sensitivity: "base",
                        })
                      )
                      .filter((dt: any) => {
                        // Quitar la opción "Lider" del select por defecto.
                        // Sin embargo, si el formulario ya tiene ese perfil seleccionado (edición), mantenerlo para no perder el valor.
                        const desc = (dt.description || "").toLowerCase().trim();
                        if (desc === "lider" && String(dt.id) !== String(formData.perfil)) return false;
                        return true;
                      })
                      .map((dt: any, index: number) => (
                        <option value={dt.id} key={dt.id}>
                          {dt.description}
                        </option>
                      ))}
                  </Form.Select>
                  {perfilHint && (
                    <div className="alert alert-info py-1 px-2 mt-1 mb-0" style={{fontSize:'0.82rem'}}>{perfilHint}</div>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    id="email"
                    value={formData.email}
                    placeholder="email"
                    onChange={textChange}
                    maxLength={100}
                    disabled={evalText === "lider"}
                  />
                </Form.Group>
              </div>
              <div className="col-lg-6">
                <Form.Group>
                  <Form.Label>Cargo</Form.Label>
                  <Form.Select
                    id="cargo"
                    onChange={select}
                    value={formData.cargo}
                    required
                  >
                    <option value="">Seleccione un cargo...</option>
                    {cargo
                      .sort((a: any, b: any) =>
                        a.description.localeCompare(b.description, "es", {
                          sensitivity: "base",
                        })
                      )
                      .map((dt: any) => (
                        <option value={dt.id} key={dt.id}>
                          {dt.description}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Cliente</Form.Label>
                  <Form.Select
                    id="cliente"
                    onChange={select}
                    value={formData.cliente}
                    disabled={clnt}
                  >
                    <option value="">Seleccione un cliente...</option>
                    {clientes.map((dt: any, index: number) => {
                      console.log(`[Select Cliente] Option: id=${dt.id}, selected=${String(dt.id) === formData.cliente}`);
                      return (
                        <option value={dt.id} key={dt.id}>
                          {dt.description}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group>
                  <Form.Label>Grupo de Negócio</Form.Label>
                  <Form.Select
                    id="empresa"
                    onChange={select}
                    value={formData.empresa}
                    disabled={clnt || emp.profile === "Evaluador"}
                  >
                    <option value="">Seleccione una empresa...</option>
                    {companies.map((dt: any, index: number) => (
                      <option value={dt.idbusiness_group} key={index}>
                        {dt.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-lg-6">
                <Form.Group>
                  <Form.Label>Fecha de ingreso</Form.Label>
                  <Form.Control
                    type="date"
                    id="fingreso"
                    value={formData.fingreso}
                    onChange={textChange}
                    max={new Date().toISOString().split("T")[0]}
                    disabled={evalText === "lider"}
                  />
                </Form.Group>
              </div>
              <div className="col-lg-6">
                <Form.Group>
                  <Form.Label>Evaluador</Form.Label>
                  {evalText === "evaluador externo" ? (
                    <Form.Control
                      type="text"
                      value="Julie Charline Manrique Rojas"
                      readOnly
                      className="bg-light"
                    />
                  ) : (
                    <Form.Select
                      id="evaluador"
                      onChange={select}
                      value={formData.evaluador}
                      disabled={evalua}
                    >
                      <option value="">Seleccione el evaluador... </option>
                      {filteredEvaluators
                        .sort((a: any, b: any) =>
                          a.evaluator.localeCompare(b.evaluator, "es", {
                            sensitivity: "base",
                          })
                        )
                        .map((dt: any, index: number) => (
                          <option value={dt.id_user} key={dt.id_user}>
                            {dt.evaluator}
                          </option>
                        ))}
                    </Form.Select>
                  )}
                </Form.Group>
              </div>
              {evalText !== "administrador" && evalText !== "evaluador" && evalText !== "evaluador externo" && (
              <div className="col-lg-6">
                <Form.Group>
                  <Form.Label>Líder</Form.Label>
                  <Form.Control
                    type="text"
                    id="lider"
                    value={formData.lider}
                    onChange={textChange}
                    placeholder="Nombre del líder"
                    disabled={evalua}
                    maxLength={150}
                  />
                </Form.Group>
              </div>
              )}
              {/* Sección Accesos — para Evaluador, Evaluador Externo y Administrador */}
              {(evalText === "evaluador" || evalText === "evaluador externo" || evalText === "administrador") && (
                <div className="col-12">
                  <hr />
                  <h6 className="fw-bold mb-3">Accesos a Paneles</h6>
                  <div className="d-flex gap-2 mb-2 flex-wrap">
                    <Form.Select
                      value={nuevoAccesoGrupo}
                      onChange={e => {
                        setNuevoAccesoGrupo(e.target.value);
                        setNuevoAccesoEvaluador("");
                        cargarEvaluadoresPorGrupo(e.target.value);
                      }}
                      style={{ maxWidth: "200px" }}
                    >
                      <option value="">Grupo de Negocio...</option>
                      {companies.map((c: any) => <option value={String(c.idbusiness_group)} key={c.idbusiness_group}>{c.name}</option>)}
                    </Form.Select>
                    <Form.Select
                      value={nuevoAccesoPerfil}
                      onChange={e => setNuevoAccesoPerfil(e.target.value)}
                      style={{ maxWidth: "180px" }}
                    >
                      <option value="">Perfil...</option>
                      {perfil.map((p: any) => <option value={p.id} key={p.id}>{p.description}</option>)}
                    </Form.Select>
                    {/* Selector de evaluador: solo aparece cuando el perfil elegido es Colaborador */}
                    {perfil.find((p: any) => String(p.id) === nuevoAccesoPerfil)?.description?.toLowerCase() === "colaborador" && (
                      <Form.Select
                        value={nuevoAccesoEvaluador}
                        onChange={e => setNuevoAccesoEvaluador(e.target.value)}
                        style={{ maxWidth: "220px" }}
                      >
                        <option value="">Evaluador que lo evaluará...</option>
                        {evaluadoresPorGrupo.map((ev: any) => (
                          <option value={String(ev.id_user)} key={ev.id_user}>{ev.evaluator}</option>
                        ))}
                      </Form.Select>
                    )}
                    <Button variant="outline-success" onClick={agregarAcceso}>+ Agregar</Button>
                  </div>
                  {accesos.length > 0 && (
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>Grupo de Negocio</th>
                          <th>Perfil</th>
                          <th>Evaluador asignado</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {accesos.map((a, i) => (
                          <tr key={i}>
                            <td>{a.client_name || getBusinessGroupName(a.idbusiness_group)}</td>
                            <td>{a.profile_name}</td>
                            <td>{a.evaluator_name || <span className="text-muted">—</span>}</td>
                            <td><Button variant="outline-danger" size="sm" onClick={() => eliminarAcceso(i)}>✕</Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}


              {modalMode === "edit" && (
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label>Estado</Form.Label>
                    <Form.Select
                      id="states"
                      value={formData.states}
                      onChange={e => setFormData({ ...formData, states: e.target.value })}
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              )}
            </div>
        
            <Modal.Footer>
              <Button variant="secondary" onClick={cancelar} disabled={isLoading}>
              Cancelar
              </Button>
            <Button 
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Procesando..." : (modalMode === "add" ? "Guardar" : "Actualizar")}
            </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
      {/************Modal cosnulta para desactivar colaboradores**********/}
      <MsgDialog
        Show={borrarShow}
        Title={`Gestión Usuarios`}
        Message={msgActivacion}
        BtnOkName="Sí, continuar"
        BtnNokName="No, cancelar"
        HandlerClickOk={() => borrarSi()}
        HanlerdClickNok={borrarNo}
      />

      {/************Modal de respuesdta a la acción realizada**********/}
      <MsgDialog
        Show={respuestaShow}
        Title={`Gestión Colaboradores`}
        Message={respuestaMsg}
        BtnOkName="Aceptar"
        BtnNokName=""
        HandlerClickOk={() => setRespuestaShow(false)}
        HanlerdClickNok={null}
      />
    </>
  );
};

export default ColaboradoresPage;